import * as React from 'react'
import { useState, useMemo } from 'react'

import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, DatePicker, Flex, Input, Select, Tag, Typography } from 'antd'
import dayjs from 'dayjs'

const { Text } = Typography
const { RangePicker } = DatePicker

type CountryOption = {
  label: string
  value: string
}

export type PartnerClientFiltersPanelProps = {
  filters: {
    clientId?: string
    email?: string
    sumCommissions?: string
    sumLots?: string
    sumCashback?: string
    verified?: boolean
    hasDeposit?: boolean
    country?: string[]
    dateFrom?: string
    dateTo?: string
  }
  setFilters: (value: any) => void
  resetFilters: () => void
  countryOptions: CountryOption[]
}

function BoolFilter({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  return (
    <Flex align="center" gap={6}>
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      <Button
        type={value === true ? 'primary' : 'default'}
        onClick={() => onChange(value === true ? undefined : true)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        Yes
      </Button>
      <Button
        type={value === false ? 'primary' : 'default'}
        danger={value === false}
        onClick={() => onChange(value === false ? undefined : false)}
        style={{ fontSize: 12, padding: '0 12px' }}
      >
        No
      </Button>
    </Flex>
  )
}

export const PartnerClientFiltersPanel = ({
  filters,
  setFilters,
  resetFilters,
  countryOptions,
}: PartnerClientFiltersPanelProps) => {
  const [inputClientId, setInputClientId] = useState(filters.clientId ?? '')
  const [inputEmail, setInputEmail] = useState(filters.email ?? '')
  const [inputSumCommissions, setInputSumCommissions] = useState(filters.sumCommissions ?? '')
  const [inputSumLots, setInputSumLots] = useState(filters.sumLots ?? '')
  const [inputSumCashback, setInputSumCashback] = useState(filters.sumCashback ?? '')

  const applySearch = (
    key: 'clientId' | 'email' | 'sumCommissions' | 'sumLots' | 'sumCashback',
    value: string,
  ) => {
    setFilters({ [key]: value.trim() || undefined, page: undefined } as any)
  }

  const clearSearch = (
    key: 'clientId' | 'email' | 'sumCommissions' | 'sumLots' | 'sumCashback',
    setter: (v: string) => void,
  ) => {
    setter('')
    setFilters({ [key]: undefined, page: undefined } as any)
  }

  const handleResetAll = () => {
    resetFilters()
    setInputClientId('')
    setInputEmail('')
    setInputSumCommissions('')
    setInputSumLots('')
    setInputSumCashback('')
  }

  const countryNameByCode = useMemo(() => {
    const map: Record<string, string> = {}
    countryOptions.forEach((c) => { map[c.value] = c.label })
    return map
  }, [countryOptions])

  const dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null =
    filters.dateFrom && filters.dateTo
      ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
      : null

  const activeTags = useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []

    if (filters.clientId)
      tags.push({
        key: 'clientId',
        label: `ID: ${filters.clientId}`,
        clear: () => clearSearch('clientId', setInputClientId),
      })
    if (filters.email)
      tags.push({
        key: 'email',
        label: `Email: ${filters.email}`,
        clear: () => clearSearch('email', setInputEmail),
      })
    if (filters.sumCommissions)
      tags.push({
        key: 'sumCommissions',
        label: `Commission: ${filters.sumCommissions}`,
        clear: () => clearSearch('sumCommissions', setInputSumCommissions),
      })
    if (filters.sumLots)
      tags.push({
        key: 'sumLots',
        label: `Lots: ${filters.sumLots}`,
        clear: () => clearSearch('sumLots', setInputSumLots),
      })
    if (filters.sumCashback)
      tags.push({
        key: 'sumCashback',
        label: `Cashback: ${filters.sumCashback}`,
        clear: () => clearSearch('sumCashback', setInputSumCashback),
      })
    if (filters.verified !== undefined)
      tags.push({
        key: 'verified',
        label: `Verified: ${filters.verified ? 'Yes' : 'No'}`,
        clear: () => setFilters({ verified: undefined, page: undefined } as any),
      })
    if (filters.hasDeposit !== undefined)
      tags.push({
        key: 'deposit',
        label: `Deposit: ${filters.hasDeposit ? 'Yes' : 'No'}`,
        clear: () => setFilters({ hasDeposit: undefined, page: undefined } as any),
      })
    if (filters.dateFrom && filters.dateTo)
      tags.push({
        key: 'date',
        label: `${filters.dateFrom} \u2014 ${filters.dateTo}`,
        clear: () =>
          setFilters({ dateFrom: undefined, dateTo: undefined, page: undefined } as any),
      })
    filters.country?.forEach((c) =>
      tags.push({
        key: `c-${c}`,
        label: countryNameByCode[c] || c,
        clear: () =>
          setFilters({
            country: filters.country?.filter((x) => x !== c),
            page: undefined,
          } as any),
      }),
    )
    return tags
  }, [filters, countryNameByCode])

  return (
    <div
      style={{
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 12,
        border: '1px solid var(--crm-border, #d9d9d9)',
        background: 'var(--crm-bg, #fff)',
      }}
    >
      <Flex gap={6} wrap="wrap" style={{ marginBottom: 10 }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Client ID"
          value={inputClientId}
          onChange={(e) => setInputClientId(e.target.value.replace(/\D/g, ''))}
          onPressEnter={() => applySearch('clientId', inputClientId)}
          onBlur={() => applySearch('clientId', inputClientId)}
          allowClear
          onClear={() => clearSearch('clientId', setInputClientId)}
          style={{ width: 130, fontSize: 14 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Email"
          value={inputEmail}
          onChange={(e) => setInputEmail(e.target.value)}
          onPressEnter={() => applySearch('email', inputEmail)}
          onBlur={() => applySearch('email', inputEmail)}
          allowClear
          onClear={() => clearSearch('email', setInputEmail)}
          style={{ width: 220 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Commission"
          value={inputSumCommissions}
          onChange={(e) => setInputSumCommissions(e.target.value)}
          onPressEnter={() => applySearch('sumCommissions', inputSumCommissions)}
          onBlur={() => applySearch('sumCommissions', inputSumCommissions)}
          allowClear
          onClear={() => clearSearch('sumCommissions', setInputSumCommissions)}
          style={{ width: 160 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Lots"
          value={inputSumLots}
          onChange={(e) => setInputSumLots(e.target.value)}
          onPressEnter={() => applySearch('sumLots', inputSumLots)}
          onBlur={() => applySearch('sumLots', inputSumLots)}
          allowClear
          onClear={() => clearSearch('sumLots', setInputSumLots)}
          style={{ width: 160 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: '#545c6e', fontSize: 12 }} />}
          placeholder="Cashback"
          value={inputSumCashback}
          onChange={(e) => setInputSumCashback(e.target.value)}
          onPressEnter={() => applySearch('sumCashback', inputSumCashback)}
          onBlur={() => applySearch('sumCashback', inputSumCashback)}
          allowClear
          onClear={() => clearSearch('sumCashback', setInputSumCashback)}
          style={{ width: 160 }}
        />
      </Flex>

      <div style={{ height: 1, background: 'var(--crm-border, #e2e5ec)', margin: '0 0 10px' }} />

      <Flex gap={10} wrap="wrap" align="center">
        <BoolFilter
          label="Verified"
          value={filters.verified}
          onChange={(v) => setFilters({ verified: v, page: undefined } as any)}
        />
        <BoolFilter
          label="Deposit"
          value={filters.hasDeposit}
          onChange={(v) => setFilters({ hasDeposit: v, page: undefined } as any)}
        />

        <div style={{ width: 1, height: 28, background: 'var(--crm-border, #e2e5ec)' }} />

        <Select
          mode="multiple"
          placeholder="Country"
          value={filters.country || []}
          onChange={(v) =>
            setFilters({ country: v.length > 0 ? v : undefined, page: undefined } as any)
          }
          options={countryOptions}
          style={{ minWidth: 180 }}
          maxTagCount="responsive"
          allowClear
          showSearch
          optionFilterProp="label"
        />

        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals && vals[0] && vals[1]) {
              setFilters({
                dateFrom: vals[0].format('YYYY-MM-DD'),
                dateTo: vals[1].format('YYYY-MM-DD'),
                page: undefined,
              } as any)
            } else {
              setFilters({ dateFrom: undefined, dateTo: undefined, page: undefined } as any)
            }
          }}
          format="YYYY-MM-DD"
          placeholder={['Attached from', 'to']}
          style={{ width: 260 }}
        />
      </Flex>

      {activeTags.length > 0 && (
        <>
          <div style={{ height: 1, background: 'var(--crm-border, #e2e5ec)', margin: '10px 0 8px' }} />
          <Flex gap={6} wrap="wrap" align="center">
            <Text
              type="secondary"
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                whiteSpace: 'nowrap',
              }}
            >
              Active:
            </Text>
            {activeTags.map((t) => (
              <Tag
                key={t.key}
                closable
                onClose={t.clear}
                style={{ fontSize: 11, margin: 0 }}
                color="blue"
              >
                {t.label}
              </Tag>
            ))}
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              onClick={handleResetAll}
              style={{ color: '#ef4444', fontSize: 11 }}
            >
              Reset all
            </Button>
          </Flex>
        </>
      )}
    </div>
  )
}
